import React, { useState, useRef, useCallback } from 'react';

export default function LoadConfigModal({ onClose, onLoad, onDelete, onRename, onExport, onImport, configurations, isPremium = true }) {
  // Track selection by a stable key (name+timestamp) to survive deletions
  const [selectedKey, setSelectedKey] = useState(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState(null);
  const [importError, setImportError] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const fileInputRef = useRef(null);

  const getKey = (config) => `${config.name}__${config.timestamp}`;

  const selectedIndex = selectedKey !== null
    ? configurations.findIndex(c => getKey(c) === selectedKey)
    : -1;

  const selectedConfig = selectedIndex >= 0 ? configurations[selectedIndex] : null;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  };

  const sortedConfigs = [...configurations].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'name') {
      cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    } else {
      cmp = new Date(a.timestamp) - new Date(b.timestamp);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const sortArrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  const handleSelect = (config) => {
    const key = getKey(config);
    setSelectedKey(prev => prev === key ? null : key); // click again to deselect
    setPendingDeleteKey(null);
  };

  const handleLoad = useCallback((config) => {
    if (config) onLoad(config);
  }, [onLoad]);

  const handleKeyDown = (e) => {
    if (configurations.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = selectedIndex < configurations.length - 1 ? selectedIndex + 1 : 0;
      setSelectedKey(getKey(configurations[next]));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = selectedIndex > 0 ? selectedIndex - 1 : configurations.length - 1;
      setSelectedKey(getKey(configurations[prev]));
    } else if (e.key === 'Enter' && selectedConfig) {
      handleLoad(selectedConfig);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        onImport(imported);
      } catch {
        setImportError('Invalid file — could not parse JSON.');
      }
    };
    reader.readAsText(file);
    // Reset input so re-importing the same file triggers onChange again
    e.target.value = '';
  };

  const handleDeleteRequest = (e, key) => {
    e.stopPropagation();
    setPendingDeleteKey(key);
  };

  const handleDeleteConfirm = (e) => {
    e.stopPropagation();
    const idx = configurations.findIndex(c => getKey(c) === pendingDeleteKey);
    if (idx >= 0) {
      onDelete(idx);
      if (selectedKey === pendingDeleteKey) setSelectedKey(null);
    }
    setPendingDeleteKey(null);
  };

  const handleDeleteCancel = (e) => {
    e.stopPropagation();
    setPendingDeleteKey(null);
  };

  const btnBase = {
    padding: '6px 12px',
    border: '1px solid #fff',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    color: 'white',
    minWidth: '110px',
    textAlign: 'center',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{ maxWidth: '620px', outline: 'none' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '12px' }}>Load Configuration</h2>

        {configurations.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', margin: '20px 0' }}>
            No saved configurations yet.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#555', marginRight: '2px' }}>Sort by:</span>
              {['name', 'date'].map(field => (
                <button
                  key={field}
                  onClick={() => handleSort(field)}
                  style={{
                    padding: '3px 10px',
                    fontSize: '12px',
                    fontWeight: sortField === field ? '700' : '500',
                    background: sortField === field ? '#007bff' : '#e9ecef',
                    color: sortField === field ? '#fff' : '#333',
                    border: '1px solid ' + (sortField === field ? '#0056b3' : '#ccc'),
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {field}{sortArrow(field)}
                </button>
              ))}
            </div>
            <div style={{ maxHeight: '360px', overflowY: 'auto', marginBottom: '12px' }}>
            {sortedConfigs.map((config) => {
              const key = getKey(config);
              const isSelected = selectedKey === key;
              const isPendingDelete = pendingDeleteKey === key;
              const cardCount = Object.values(config.cardSelections).filter(arr => arr.length > 0).length;
              const denomLabel = config.denomination >= 1
                ? `$${config.denomination}`
                : `${Math.round(config.denomination * 100)}¢`;

              return (
                <div
                  key={key}
                  onClick={() => handleSelect(config)}
                  onDoubleClick={() => handleLoad(config)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    border: isSelected ? '2px solid #007bff' : isPendingDelete ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '6px',
                    backgroundColor: isPendingDelete ? '#fff5f5' : isSelected ? '#e7f3ff' : '#f9f9f9',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '3px', fontSize: '14px' }}>
                      {config.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#555', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <span>🕒 {new Date(config.timestamp).toLocaleString()}</span>
                      <span>🃏 Cards: {cardCount}</span>
                      <span>📋 Table: {config.activePaytable}</span>
                      <span>💰 Denom: {denomLabel}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '4px', marginLeft: '8px', alignItems: 'center' }}>
                    {isPendingDelete ? (
                      <>
                        <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 'bold', marginRight: '4px' }}>Delete?</span>
                        <button
                          onClick={handleDeleteConfirm}
                          style={{ ...btnBase, background: '#dc3545', minWidth: 'auto', padding: '4px 10px', fontSize: '12px' }}
                          title="Confirm delete"
                        >
                          Yes
                        </button>
                        <button
                          onClick={handleDeleteCancel}
                          style={{ ...btnBase, background: '#6c757d', minWidth: 'auto', padding: '4px 10px', fontSize: '12px' }}
                          title="Cancel"
                        >
                          No
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); onRename(configurations.findIndex(c => getKey(c) === key), config.name); }}
                          style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
                          title="Rename"
                        >✏️</button>
                        <button
                          onClick={(e) => handleDeleteRequest(e, key)}
                          style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
                          title="Delete"
                        >🗑️</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}

        {importError && (
          <div style={{ color: '#dc3545', fontSize: '12px', marginBottom: '8px', padding: '6px 10px', background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '4px' }}>
            ⚠️ {importError}
          </div>
        )}

        <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>
          Click to select · Double-click to load · ↑↓ to navigate · Enter to load
        </div>

        <div style={{ borderTop: '1px solid #ddd', paddingTop: '12px' }}>
          {!isPremium && (
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', padding: '5px 8px', background: '#fffbea', border: '1px solid #ffd600', borderRadius: '4px' }}>
              ★ Export and Import are <a href="/pricing" style={{ color: '#b8860b', fontWeight: '700' }}>Premium</a> features.
            </div>
          )} 
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={onExport}
                className="btn"
                disabled={configurations.length === 0 || !isPremium}
                style={{ minWidth: '100px', opacity: (configurations.length === 0 || !isPremium) ? 0.5 : 1, cursor: (configurations.length === 0 || !isPremium) ? 'not-allowed' : 'pointer' }}
                title={!isPremium ? 'Premium feature — upgrade to export configurations' : 'Export all configurations to a JSON file'}
              >
                Export All
              </button>
              <button
                onClick={() => selectedConfig && onExport(selectedConfig)}
                className="btn"
                disabled={!selectedConfig || !isPremium}
                style={{ minWidth: '100px', background: '#17a2b8', opacity: (selectedConfig && isPremium) ? 1 : 0.5, cursor: (selectedConfig && isPremium) ? 'pointer' : 'not-allowed' }}
                title={!isPremium ? 'Premium feature — upgrade to export configurations' : 'Export only the selected configuration'}
              >
                Export Selected
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => { if (!isPremium) return; setImportError(null); fileInputRef.current?.click(); }}
                className="btn"
                disabled={!isPremium}
                style={{ minWidth: '100px', opacity: isPremium ? 1 : 0.5, cursor: isPremium ? 'pointer' : 'not-allowed' }}
                title={!isPremium ? 'Premium feature — upgrade to import configurations' : 'Import configurations from a JSON file'}
              >
                Import
              </button>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => selectedConfig && handleLoad(selectedConfig)}
                className="btn"
                disabled={!selectedConfig}
                style={{ minWidth: '100px', background: selectedConfig ? '#28a745' : '#6c757d', opacity: selectedConfig ? 1 : 0.5, cursor: selectedConfig ? 'pointer' : 'not-allowed' }}
              >
                Load
              </button>
              <button onClick={onClose} className="btn" style={{ minWidth: '100px', background: '#dc3545' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
